import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import config from '../config/config';
import { useAuth } from '../context/AuthContext';
import './VigilConceptBrowser.css';

/* ── category palette ──────────────────────────────────────────────────────── */
const CAT = {
  source:      { color: '#3b82f6', icon: '📥' },
  sink:        { color: '#ef4444', icon: '📤' },
  sanitizer:   { color: '#22c55e', icon: '🧹' },
  guard:       { color: '#8b5cf6', icon: '🛡️' },
  crypto:      { color: '#f59e0b', icon: '🔐' },
  auth:        { color: '#6366f1', icon: '🔑' },
  pii:         { color: '#ec4899', icon: '👤' },
  credentials: { color: '#f97316', icon: '🔒' },
};

const LANG_CLASS = {
  go: 'go', java: 'java', python: 'python', javascript: 'javascript',
  typescript: 'typescript', c: 'c', 'c++': 'cpp', cpp: 'cpp',
};

const TABS = ['all', 'source', 'sink', 'sanitizer', 'guard', 'crypto', 'auth', 'pii'];

/* ── animated counter ──────────────────────────────────────────────────────── */
function AnimatedCounter({ value, duration = 600 }) {
  const ref = useRef(null);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = value;
    if (start === end) return;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      if (ref.current) ref.current.textContent = Math.round(start + (end - start) * ease);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    prev.current = end;
  }, [value, duration]);
  return <span ref={ref}>0</span>;
}

/* ── mock data (used when API is unavailable) ──────────────────────────────── */
function getMockConcepts() {
  return [
    { id: 'sql_execute', name: 'SQL Execute', description: 'Executes raw SQL queries against a database, potentially vulnerable to SQL injection if inputs are not parameterized.', category: 'sink', tags: ['sql', 'injection', 'database'], patterns: { go: ['db.Query()', 'db.Exec()'], java: ['Statement.execute()', 'PreparedStatement.executeQuery()'], python: ['cursor.execute()', 'engine.execute()'], javascript: ['pool.query()', 'connection.query()'] } },
    { id: 'http_request_input', name: 'HTTP Request Input', description: 'User-controlled input from HTTP request parameters, headers, or body content.', category: 'source', tags: ['http', 'input', 'web'], patterns: { go: ['r.URL.Query()', 'r.FormValue()', 'r.Body'], java: ['request.getParameter()', 'request.getHeader()'], python: ['request.args.get()', 'request.form[]'], javascript: ['req.query', 'req.body', 'req.params'] } },
    { id: 'html_encode', name: 'HTML Encode', description: 'Sanitizes user input by encoding HTML special characters to prevent cross-site scripting (XSS) attacks.', category: 'sanitizer', tags: ['xss', 'encoding', 'html'], patterns: { go: ['html.EscapeString()', 'template.HTMLEscapeString()'], java: ['StringEscapeUtils.escapeHtml4()', 'HtmlUtils.htmlEscape()'], python: ['markupsafe.escape()', 'html.escape()'], javascript: ['DOMPurify.sanitize()', 'escapeHtml()'] } },
    { id: 'auth_check', name: 'Authentication Check', description: 'Verifies user authentication status before allowing access to protected resources.', category: 'guard', tags: ['authentication', 'middleware', 'access-control'], patterns: { go: ['auth.Middleware()', 'jwt.Parse()'], java: ['@PreAuthorize', 'SecurityContextHolder.getContext()'], python: ['@login_required', 'current_user.is_authenticated'], javascript: ['passport.authenticate()', 'jwt.verify()'] } },
    { id: 'aes_encrypt', name: 'AES Encryption', description: 'Symmetric encryption using Advanced Encryption Standard (AES) algorithm.', category: 'crypto', tags: ['encryption', 'symmetric', 'aes'], patterns: { go: ['aes.NewCipher()', 'cipher.NewGCM()'], java: ['Cipher.getInstance("AES")', 'SecretKeySpec()'], python: ['AES.new()', 'Fernet()'], javascript: ['crypto.createCipheriv()', 'subtle.encrypt()'] } },
    { id: 'jwt_sign', name: 'JWT Signing', description: 'Creates and signs JSON Web Tokens for authentication and authorization.', category: 'auth', tags: ['jwt', 'token', 'signing'], patterns: { go: ['jwt.NewWithClaims()', 'token.SignedString()'], java: ['Jwts.builder().signWith()', 'JWT.create()'], python: ['jwt.encode()', 'create_access_token()'], javascript: ['jwt.sign()', 'jsonwebtoken.sign()'] } },
    { id: 'email_address', name: 'Email Address', description: 'Personally identifiable information: email address fields that require protection under GDPR/privacy regulations.', category: 'pii', tags: ['email', 'gdpr', 'privacy'], patterns: { go: ['user.Email', 'profile.EmailAddress'], java: ['getEmail()', 'user.getEmailAddress()'], python: ['user.email', 'profile.email_address'], javascript: ['user.email', 'formData.email'] } },
    { id: 'command_execute', name: 'Command Execute', description: 'Executes system commands, vulnerable to command injection if inputs are not sanitized.', category: 'sink', tags: ['command-injection', 'os', 'exec'], patterns: { go: ['exec.Command()', 'os.StartProcess()'], java: ['Runtime.exec()', 'ProcessBuilder.start()'], python: ['os.system()', 'subprocess.run()'], javascript: ['child_process.exec()', 'execSync()'] } },
    { id: 'file_read', name: 'File Read', description: 'Reads file contents from disk, vulnerable to path traversal if file paths are user-controlled.', category: 'source', tags: ['file', 'path-traversal', 'io'], patterns: { go: ['os.ReadFile()', 'ioutil.ReadFile()'], java: ['FileInputStream()', 'Files.readAllBytes()'], python: ['open()', 'Path.read_text()'], javascript: ['fs.readFile()', 'fs.readFileSync()'] } },
    { id: 'sql_parameterize', name: 'SQL Parameterize', description: 'Parameterized SQL query construction that prevents SQL injection by separating queries from data.', category: 'sanitizer', tags: ['sql', 'parameterized', 'safe'], patterns: { go: ['db.Query(query, args...)', 'sqlx.NamedExec()'], java: ['PreparedStatement.setString()', 'NamedParameterJdbcTemplate'], python: ['cursor.execute(query, params)', 'text().bindparams()'], javascript: ['pool.query(text, values)', 'knex.where()'] } },
    { id: 'rate_limiter', name: 'Rate Limiter', description: 'Guards against brute-force and denial-of-service attacks by limiting request frequency.', category: 'guard', tags: ['rate-limit', 'dos', 'throttle'], patterns: { go: ['rate.NewLimiter()', 'tollbooth.NewLimiter()'], java: ['RateLimiter.create()', '@RateLimit'], python: ['Limiter()', 'throttle_classes'], javascript: ['rateLimit()', 'express-rate-limit'] } },
    { id: 'password_hash', name: 'Password Hash', description: 'Cryptographic hashing of passwords using secure algorithms like bcrypt, argon2, or scrypt.', category: 'crypto', tags: ['password', 'hashing', 'bcrypt'], patterns: { go: ['bcrypt.GenerateFromPassword()', 'argon2.IDKey()'], java: ['BCryptPasswordEncoder.encode()', 'Argon2PasswordEncoder'], python: ['bcrypt.hashpw()', 'make_password()'], javascript: ['bcrypt.hash()', 'argon2.hash()'] } },
    { id: 'oauth_token', name: 'OAuth Token', description: 'OAuth 2.0 access token handling for delegated authorization flows.', category: 'auth', tags: ['oauth', 'token', 'authorization'], patterns: { go: ['oauth2.Config.Exchange()', 'token.AccessToken'], java: ['OAuth2AuthorizedClient', 'getAccessToken()'], python: ['oauth.fetch_token()', 'session.token'], javascript: ['oauth2Client.getToken()', 'passport-oauth2'] } },
    { id: 'ssn_field', name: 'Social Security Number', description: 'Highly sensitive PII: Social Security Number fields requiring encryption at rest and in transit.', category: 'pii', tags: ['ssn', 'sensitive', 'encryption-required'], patterns: { go: ['user.SSN', 'record.SocialSecurityNumber'], java: ['getSSN()', 'getSocialSecurityNumber()'], python: ['user.ssn', 'record.social_security_number'], javascript: ['user.ssn', 'formData.socialSecurityNumber'] } },
    { id: 'env_variable', name: 'Environment Variable', description: 'Reads environment variables which may contain secrets, API keys, or configuration values.', category: 'source', tags: ['environment', 'config', 'secrets'], patterns: { go: ['os.Getenv()', 'os.LookupEnv()'], java: ['System.getenv()', 'System.getProperty()'], python: ['os.environ.get()', 'os.getenv()'], javascript: ['process.env', 'dotenv.config()'] } },
    { id: 'csrf_token', name: 'CSRF Token', description: 'Cross-Site Request Forgery protection guard that validates anti-CSRF tokens on state-changing requests.', category: 'guard', tags: ['csrf', 'token', 'web-security'], patterns: { go: ['csrf.Protect()', 'gorilla/csrf.Token()'], java: ['CsrfFilter', '@CsrfToken'], python: ['csrf_protect', '@csrf_exempt'], javascript: ['csurf()', 'csrfToken()'] } },
    { id: 'api_key', name: 'API Key', description: 'Credential patterns for API keys that must be protected and never hardcoded in source.', category: 'credentials', tags: ['api-key', 'secret', 'credential'], patterns: { go: ['apiKey := os.Getenv()', 'config.APIKey'], java: ['getApiKey()', '@Value(api.key)'], python: ['API_KEY = os.environ.get()', 'settings.API_KEY'], javascript: ['process.env.API_KEY', 'config.apiKey'] } },
    { id: 'tls_config', name: 'TLS Configuration', description: 'Transport Layer Security setup for encrypted communications, including cipher suite and protocol version selection.', category: 'crypto', tags: ['tls', 'ssl', 'transport'], patterns: { go: ['tls.Config{}', 'tls.Listen()'], java: ['SSLContext.getInstance()', 'TrustManagerFactory'], python: ['ssl.create_default_context()', 'SSLContext()'], javascript: ['https.createServer()', 'tls.connect()'] } },
  ];
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  VigilConceptBrowser                                                       */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function VigilConceptBrowser() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [flowOpen, setFlowOpen] = useState(false);

  /* ── fetch concepts ──────────────────────────────────────────────────────── */
  const fetchConcepts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/concepts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConcepts(data.concepts || data || []);
    } catch {
      setConcepts(getMockConcepts());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchConcepts(); }, [fetchConcepts]);

  /* ── derived data ────────────────────────────────────────────────────────── */
  const counts = useMemo(() => {
    const c = { total: concepts.length };
    TABS.forEach(tab => { if (tab !== 'all') c[tab] = 0; });
    concepts.forEach(con => { if (c[con.category] !== undefined) c[con.category]++; });
    // Also count credentials for stats but it's not a tab
    c.credentials = concepts.filter(con => con.category === 'credentials').length;
    return c;
  }, [concepts]);

  const filtered = useMemo(() => {
    let list = concepts;
    if (activeTab !== 'all') list = list.filter(c => c.category === activeTab);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.id.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q) ||
        (c.tags || []).some(tag => tag.toLowerCase().includes(q))
      );
    }
    return list;
  }, [concepts, activeTab, search]);

  /* ── handlers ────────────────────────────────────────────────────────────── */
  const handleCopy = useCallback((conceptId, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`@${conceptId}`).then(() => {
      setCopiedId(conceptId);
      setTimeout(() => setCopiedId(null), 1500);
    });
  }, []);

  const handleFlowClick = useCallback((category) => {
    setActiveTab(category);
    setFlowOpen(false);
  }, []);

  /* ── stat cards config ───────────────────────────────────────────────────── */
  const statCards = [
    { key: 'total', label: t('vigilConcepts.totalConcepts'), icon: '📚', cls: 'total' },
    { key: 'source', label: t('vigilConcepts.sources'), icon: '📥', cls: 'source' },
    { key: 'sink', label: t('vigilConcepts.sinks'), icon: '📤', cls: 'sink' },
    { key: 'sanitizer', label: t('vigilConcepts.sanitizers'), icon: '🧹', cls: 'sanitizer' },
    { key: 'guard', label: t('vigilConcepts.guards'), icon: '🛡️', cls: 'guard' },
    { key: 'crypto', label: t('vigilConcepts.crypto'), icon: '🔐', cls: 'crypto' },
  ];

  /* ── render ──────────────────────────────────────────────────────────────── */
  return (
    <div className="vcb">
      {/* Header */}
      <div className="ce-page-header mb-4">
        <h2 className="ce-page-title">📖 {t('vigilConcepts.title')}</h2>
        <p style={{ color: 'var(--bs-secondary-color)' }}>{t('vigilConcepts.subtitle')}</p>
      </div>

      {/* Stats row */}
      <div className="ce-kpi-row mb-4">
        {statCards.map(s => (
          <motion.div
            key={s.key}
            className="ce-kpi"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: statCards.indexOf(s) * 0.06 }}
          >
            <div className="ce-kpi-value">{s.icon} <AnimatedCounter value={counts[s.key] || 0} /></div>
            <div className="ce-kpi-label">{s.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Data Flow Diagram (collapsible) */}
      <div className="vcb-flow-toggle">
        <div className="vcb-flow-header" onClick={() => setFlowOpen(o => !o)}>
          <span>🔀 {t('vigilConcepts.dataFlow')}</span>
          <span className={`vcb-flow-chevron${flowOpen ? ' open' : ''}`}>›</span>
        </div>
        <AnimatePresence>
          {flowOpen && (
            <motion.div
              className="vcb-flow-diagram"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <div className="vcb-flow-col">
                <div className="vcb-flow-box vcb-flow-box--sanitizer" onClick={() => handleFlowClick('sanitizer')}>
                  <div className="vcb-flow-box-label">{t('vigilConcepts.sanitizers')}</div>
                  <div className="vcb-flow-box-count">{counts.sanitizer || 0}</div>
                </div>
              </div>
              <div className="vcb-flow-arrow">↑</div>
              <div className="vcb-flow-box vcb-flow-box--source" onClick={() => handleFlowClick('source')}>
                <div className="vcb-flow-box-label">{t('vigilConcepts.sources')}</div>
                <div className="vcb-flow-box-count">{counts.source || 0}</div>
              </div>
              <div className="vcb-flow-arrow">→→→</div>
              <div className="vcb-flow-box vcb-flow-box--sink" onClick={() => handleFlowClick('sink')}>
                <div className="vcb-flow-box-label">{t('vigilConcepts.sinks')}</div>
                <div className="vcb-flow-box-count">{counts.sink || 0}</div>
              </div>
              <div className="vcb-flow-arrow">↓</div>
              <div className="vcb-flow-col">
                <div className="vcb-flow-box vcb-flow-box--guard" onClick={() => handleFlowClick('guard')}>
                  <div className="vcb-flow-box-label">{t('vigilConcepts.guards')}</div>
                  <div className="vcb-flow-box-count">{counts.guard || 0}</div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Category tabs */}
      <div className="vcb-tabs">
        {TABS.map(tab => {
          const tabColor = tab === 'all' ? 'var(--accent, #6366f1)' : (CAT[tab]?.color || '#6b7280');
          const count = tab === 'all' ? counts.total : (counts[tab] || 0);
          return (
            <button
              key={tab}
              className={`vcb-tab${activeTab === tab ? ' active' : ''}`}
              style={{ '--tab-color': tabColor }}
              onClick={() => setActiveTab(tab)}
            >
              {t(`vigilConcepts.${tab === 'all' ? 'all' : tab + 's'}`, t(`vigilConcepts.${tab}`, tab.charAt(0).toUpperCase() + tab.slice(1)))}
              <span className="vcb-tab-badge">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search bar */}
      <div className="ce-filter-bar mb-3">
        <input
          type="text"
          className="ce-search-input"
          placeholder={t('vigilConcepts.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span className="ce-filter-count">{filtered.length} concepts</span>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="ce-blank">
          <span className="spinner-border spinner-border-sm me-2" />
          Loading concepts…
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="ce-blank">
          <div className="ce-blank-icon">📖</div>
          <div className="ce-blank-title">{t('vigilConcepts.noConcepts')}</div>
        </div>
      )}

      {/* Concept grid */}
      {!loading && filtered.length > 0 && (
        <div className="vcb-grid">
          <AnimatePresence>
            {filtered.map((concept, i) => (
              <motion.div
                key={concept.id}
                className={`ce-card vcb-card--${concept.category}${expandedId === concept.id ? ' expanded' : ''}`}
                onClick={() => setExpandedId(expandedId === concept.id ? null : concept.id)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, delay: i * 0.03 }}
                layout
                style={{ cursor: 'pointer' }}
              >
                {/* Card header */}
                <div className="ce-card-header" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--bs-secondary-color)' }}>@{concept.id}</span>
                  <span className="ce-tag" style={{ background: (CAT[concept.category]?.color || '#6b7280') + '22', color: CAT[concept.category]?.color || '#6b7280', border: `1px solid ${CAT[concept.category]?.color || '#6b7280'}55` }}>
                    {concept.category}
                  </span>
                </div>
                <div className="ce-card-body">
                {/* Name & description */}
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.4rem' }}>{concept.name}</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)', marginBottom: '0.5rem' }}>{concept.description}</p>

                {/* Tags */}
                {concept.tags && concept.tags.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {concept.tags.map(tag => (
                      <span key={tag} className="ce-tag">{tag}</span>
                    ))}
                  </div>
                )}

                {/* Expand hint */}
                {expandedId !== concept.id && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--bs-secondary-color)', marginTop: '0.5rem' }}>Click to expand patterns ↓</div>
                )}

                {/* Expanded: language patterns */}
                <AnimatePresence>
                  {expandedId === concept.id && concept.patterns && (
                    <motion.div
                      className="vcb-patterns-panel"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="vcb-patterns-title">{t('vigilConcepts.patterns')}</div>
                      {Object.entries(concept.patterns).map(([lang, patterns]) => (
                        <div key={lang} className="vcb-lang-section">
                          <span className={`vcb-lang-header vcb-lang-${LANG_CLASS[lang.toLowerCase()] || lang.toLowerCase()}`}>
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                          </span>
                          <div className="vcb-code-block">
                            {patterns.join('\n')}
                          </div>
                        </div>
                      ))}
                      <button
                        className={`ce-btn ce-btn--secondary ce-btn--sm${copiedId === concept.id ? ' active' : ''}`}
                        onClick={(e) => handleCopy(concept.id, e)}
                      >
                        {copiedId === concept.id ? `✓ ${t('vigilConcepts.copied')}` : `📋 ${t('vigilConcepts.useInEditor')}`}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
